/**
 * Tutor chip with nested matched-student chips. Droppable target for matching.
 */

import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { GraduationCap, X, CheckCircle, Clock } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { RemovableParticipantChip } from '@/components/common/RemovableParticipantChip';
import { cn } from '@/lib/utils';
import type { UserWithProfile } from '@/types/user.types';
import { getErrorMessage } from '@/utils/errors-utils';
import { getRoleLabel } from '@/utils/role-label-utils';
import type { SlotAvailabilityById, SlotAvailabilityStatus } from '@/types/slot-availability.types';
import { formatConflictHint } from '@/components/pairs/inperson/matching-utils';
import type { ParticipantAttendanceById } from '@/types/attendance.types';
import { ATTENDANCE_STATUS_LABELS } from '@/constants/shared-catalog';
import { ConflictBadge } from '@/components/common/ConflictBadge';
/** Props for {@link TutorChipWithMatches}. */
interface TutorChipWithMatchesProps {
  tutor: UserWithProfile;
  matchedStudents: Array<UserWithProfile & { assignmentSource?: string }>;
  onRemove: () => void;
  sessionId: string;
  onStudentRemove?: (studentId: string) => void;
  /** Hide the in-chip match count (the total stays outside the chip). */
  hideMatchCountBadge?: boolean;
  slotAvailability?: SlotAvailabilityStatus;
  slotConflictHint?: string;
  studentAvailabilityById?: SlotAvailabilityById;
  attendanceByUserId?: ParticipantAttendanceById;
}

/** Human-readable assignment source (auto pair, balanced, or history). */
const getAssignmentSourceLabel = (source?: string): string => {
  if (source === 'auto_binome_coeur') return 'Binôme cœur';
  if (source === 'auto_balanced') return 'Équilibré';
  return 'Historique';
};

/** Tutor chip that also lists matched students. */
export const TutorChipWithMatches: React.FC<TutorChipWithMatchesProps> = ({
  tutor,
  matchedStudents,
  onRemove,
  sessionId,
  onStudentRemove,
  hideMatchCountBadge = false,
  slotAvailability,
  slotConflictHint,
  studentAvailabilityById,
  attendanceByUserId,
}) => {
  const dropId = `tutor-chip-${tutor.id}-${sessionId}`;

  const { setNodeRef, isOver, active } = useDroppable({
    id: dropId,
    data: { tutorId: tutor.id, sessionId, type: 'tutor-chip' },
  });

  // Accept both drag payloads: DraggableUserCard (`roleGroup: 'students'`) and RemovableParticipantChip (`type: 'student'`).
  const activeDragData = active?.data?.current;
  const activeDragType = activeDragData?.type as string | undefined;
  const activeDragRoleGroup = activeDragData?.roleGroup as string | undefined;
  const isStudentDragging = activeDragType === 'student' || activeDragRoleGroup === 'students';
  const shouldShowDropEffect = isOver && isStudentDragging;

  const hasMatches = matchedStudents.length > 0;
  const hasSlotConflict = slotAvailability === 'conflict';
  const isSlotChecking = slotAvailability === 'checking';
  const tutorAttendance = attendanceByUserId?.[tutor.id];

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'tutor-chip-with-matches',
        shouldShowDropEffect && 'tutor-chip-drop-over',
        hasMatches && 'tutor-chip-has-matches',
        hasSlotConflict &&
          'border-[rgb(var(--color-warning))] bg-[rgb(var(--color-warning)_/_0.06)]',
      )}
    >
      <div className="tutor-chip-header">
        <div className="flex items-center gap-2 flex-1">
          <div
            className="w-6 h-6 rounded-lg flex items-center justify-center border"
            style={{
              background: 'color-mix(in srgb, var(--color-2-nuance-1) 15%, transparent)',
              borderColor: 'var(--color-2-nuance-1)',
            }}
          >
            <GraduationCap className="w-3.5 h-3.5" style={{ color: 'var(--color-2-nuance-1)' }} />
          </div>
          <span className="font-medium text-sm">
            {`${tutor.firstName} ${tutor.lastName}`.trim() || getRoleLabel('tutor')}
          </span>
          {tutorAttendance?.status === 'present' && (
            <div
              className="inline-flex items-center justify-center w-5 h-5 rounded-full border flex-shrink-0"
              style={{
                backgroundColor: 'color-mix(in srgb, rgb(10, 134, 66) 15%, transparent)',
                borderColor: 'rgb(10, 134, 66)',
              }}
              title={ATTENDANCE_STATUS_LABELS.present}
              aria-label={ATTENDANCE_STATUS_LABELS.present}
            >
              <CheckCircle className="w-3 h-3" style={{ color: 'rgb(10, 134, 66)' }} />
            </div>
          )}
          {tutorAttendance && tutorAttendance.status !== 'present' && (
            <span className="text-[10px] text-muted-foreground">
              {ATTENDANCE_STATUS_LABELS[tutorAttendance.status]}
            </span>
          )}
          {isSlotChecking && (
            <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden />
          )}
          {hasSlotConflict && (
            <ConflictBadge
              hint={slotConflictHint ?? getErrorMessage('unavailableAtSlot')}
              side="right"
            />
          )}
          {matchedStudents.length > 0 && !hideMatchCountBadge && (
            <span
              className="text-xs font-bold px-1.5 py-0.5 rounded-full border"
              style={{
                background: 'color-mix(in srgb, var(--color-3-nuance-1) 15%, transparent)',
                color: 'var(--color-3-nuance-1)',
                borderColor: 'var(--color-3-nuance-1)',
              }}
            >
              {matchedStudents.length}
            </span>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          icon={X}
          iconOnly
          onClick={onRemove}
          className="tutor-chip-remove-btn"
          aria-label="Retirer tuteur"
        />
      </div>

      {hasMatches && (
        <div className="tutor-chip-students">
          {matchedStudents.map((student) => {
            const availability = studentAvailabilityById?.[student.id];
            const studentSlotAvailability = availability?.status;
            const studentSlotConflictHint = formatConflictHint(availability?.conflictingSession);
            const attendance = attendanceByUserId?.[student.id];
            return (
              <div key={student.id} className="tutor-chip-student">
                <div className="flex items-center gap-1.5 w-full">
                  <RemovableParticipantChip
                    user={student}
                    role="student"
                    onRemove={() => onStudentRemove?.(student.id)}
                    size="sm"
                    draggable={true}
                    slotAvailability={studentSlotAvailability}
                    slotConflictHint={studentSlotConflictHint}
                    attendanceStatus={attendance?.status}
                    absenceJustification={attendance?.justification}
                  />
                  {student.assignmentSource && student.assignmentSource !== 'manual' && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap"
                      style={{
                        background:
                          student.assignmentSource === 'auto_binome_coeur'
                            ? 'color-mix(in srgb, var(--color-1-nuance-1) 12%, transparent)'
                            : 'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
                        color:
                          student.assignmentSource === 'auto_binome_coeur'
                            ? 'var(--color-1-nuance-1)'
                            : 'var(--color-3-nuance-1)',
                        borderColor:
                          student.assignmentSource === 'auto_binome_coeur'
                            ? 'var(--color-1-nuance-1)'
                            : 'var(--color-3-nuance-1)',
                      }}
                      title={getAssignmentSourceLabel(student.assignmentSource)}
                    >
                      {student.assignmentSource === 'auto_binome_coeur' ? '♥' : '⚖'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!hasMatches && shouldShowDropEffect && (
        <div className="tutor-chip-drop-placeholder">
          <p className="text-xs text-muted-foreground">Déposez un élève ici</p>
        </div>
      )}
    </div>
  );
};
